import { useEffect, useState } from "react";
import { Sparkles, SquarePen } from "lucide-react";
import { api, errMsg, toastErr, API_BASE } from "@/lib/api";
import { MessageList } from "@/components/chat/MessageList";
import { InputBar } from "@/components/chat/InputBar";

const sessionId = () => {
  let id = localStorage.getItem("chat_session");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("chat_session", id);
  }
  return id;
};

export default function Chat() {
  const [cfg, setCfg] = useState({ bot_name: "", ready: true });
  const [messages, setMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("chat_lang") || "English");
  const changeLang = (l) => { setLang(l); localStorage.setItem("chat_lang", l); };
  const newChat = () => {
    if (messages.length && !window.confirm("Start a new chat? The current conversation will be cleared.")) return;
    localStorage.removeItem("chat_session");
    setMessages([]);
  };

  useEffect(() => {
    api.get("/public/config").then((r) => {
      setCfg(r.data);
      document.title = r.data.bot_name;
    }).catch(toastErr);
    api.get("/chat/history", { params: { session_id: sessionId() } }).then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  const send = async (text) => {
    setMessages((m) => [...m, { role: "user", content: text }]);
    setThinking(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId(), message: text, language: lang });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, sources: r.data.sources }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", error: true, content: errMsg(e) }]);
    } finally {
      setThinking(false);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-50 sm:max-w-3xl mx-auto sm:border-x border-slate-200/70">
      <header data-testid="public-chat-header" className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200/80 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-slate-900 text-white grid place-items-center overflow-hidden shrink-0">
          {cfg.logo_url ? <img data-testid="bot-logo" src={`${API_BASE}${cfg.logo_url}`} alt="" className="w-full h-full object-cover" /> : <Sparkles size={16} />}
        </div>
        <div className="leading-tight">
          <h1 data-testid="bot-name-heading" className="text-base font-semibold text-slate-900">{cfg.bot_name || "…"}</h1>
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.ready ? "bg-emerald-500" : "bg-amber-400"}`} />
            {cfg.ready ? "Online" : "Not configured"}
          </p>
        </div>
        <button data-testid="new-chat-button" onClick={newChat} aria-label="New chat" title="New chat" className="ml-auto w-9 h-9 rounded-full grid place-items-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
          <SquarePen size={18} />
        </button>
        <select
          data-testid="language-select"
          value={lang}
          onChange={(e) => changeLang(e.target.value)}
          aria-label="Language"
          className="text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-3 py-1.5 outline-none cursor-pointer"
        >
          <option>English</option>
          <option>Hindi</option>
          <option>Hinglish</option>
        </select>
      </header>
      <MessageList messages={messages} thinking={thinking} botName={cfg.bot_name} lang={lang} />
      <InputBar onSend={send} disabled={thinking} lang={lang} />
    </div>
  );
}
